import { createContext, useContext, type ReactNode } from "react";
import { useOptionalBookingContext } from "./provider";
import { PEWTER_CSS } from "./internal-ui/pewter-shell";

/**
 * Which look the booking surfaces wear.
 *
 * "classic" is the package as it has always rendered, and the default: a
 * component outside any scope, or on a site that never opts in, reads it
 * from the context's own default and needs no provider at all. That is what
 * keeps the eleven city sites untouched while one activity on the main site
 * tries the pewter frame.
 *
 * Each page sets the scope for itself from the slug it already has, so the
 * flag never has to be threaded through the sidebar or the sheet: a component
 * anywhere underneath asks `useBookingSkin()`.
 */
export type BookingSkin = "classic" | "pewter";

const SkinContext = createContext<BookingSkin>("classic");

export function useBookingSkin(): BookingSkin {
  return useContext(SkinContext);
}

/**
 * The skin this activity wears, per the host app's opt-in list. Classic when
 * there is no provider at all, which is how the page tests render.
 */
export function useBookingSkinFor(slug: string | null | undefined): BookingSkin {
  const ctx = useOptionalBookingContext();
  if (ctx?.pewterAll) return "pewter";
  return slug && ctx?.pewterSlugs?.includes(slug) ? "pewter" : "classic";
}

/**
 * Sets the skin for everything inside. The pewter skin carries a small
 * stylesheet for the pieces that need real CSS states (a field's focus, a
 * pressed chip), injected here rather than shipped in an app stylesheet the
 * city sites do not load.
 */
export function BookingSkinScope({ skin, children }: { skin: BookingSkin; children: ReactNode }) {
  return (
    <SkinContext.Provider value={skin}>
      {skin === "pewter" ? <style>{PEWTER_CSS}</style> : null}
      {children}
    </SkinContext.Provider>
  );
}
